// client/src/components/plataforma/siteInstitucional/BannersHomePage.js
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBanners } from '../../../hooks/useBanners';
import { Plus, Image, Edit, Trash2, Loader2, GripVertical, MoreVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';
import useAlert from '../../../hooks/useAlert';
import { useHomePersonalizadaGate, HomePersonalizadaUpsellBanner } from './useHomePersonalizadaGate';

const BannersHomePage = () => {
    const navigate = useNavigate();
    const { hasHomePersonalizada } = useHomePersonalizadaGate();
    const { banners, setBanners, loading, error, deleteBanner, updateBannersOrder } = useBanners();
    const { setAlert } = useAlert();
    const [selectedBanner, setSelectedBanner] = useState(null);

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este banner?')) {
            try {
                await deleteBanner(id);
                setAlert('Banner excluído com sucesso!', 'success');
                setSelectedBanner(null);
            } catch (err) {
                setAlert(`Erro ao excluir banner: ${err.message}`, 'error');
            }
        }
    };

    const handleEdit = (banner) => {
        setSelectedBanner(null);
        navigate(`/site-institucional/banners/editar/${banner._id}`);
    };

    const onDragEnd = (result) => {
        const { destination, source } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }

        const items = Array.from(banners);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        const updatedItems = items.map((item, index) => ({ ...item, pedido: index }));

        setBanners(updatedItems);
        updateBannersOrder(updatedItems).catch(err => {
            setAlert(`Erro ao salvar a nova ordem: ${err.message}`, 'error');
            setBanners(banners); // Reverte para o estado anterior em caso de erro
        });
    };
    
    const sortedBanners = useMemo(() => 
        [...banners].sort((a, b) => a.pedido - b.pedido),
        [banners]
    );

    if (loading) {
        return (
            <div className="page-management-container flex justify-center items-center h-64">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
            </div>
        );
    }
    
    if (error) {
        return <div className="page-management-container"><div className="alert alert-danger">{error}</div></div>;
    }

    return (
        <div className="page-management-container">
            <PageHeader
                title="Gerenciador de Banners"
                icon={<Image className="icon-title" />}
                navBack={{ to: '/site-institucional', text: 'Voltar' }}
            />

            {!hasHomePersonalizada && <HomePersonalizadaUpsellBanner />}

            <div className="content-panel mt-6">
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="banners-list" isDropDisabled={!hasHomePersonalizada}>
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {sortedBanners.map((banner, index) => (
                                    <Draggable key={banner._id} draggableId={banner._id} index={index} isDragDisabled={!hasHomePersonalizada}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps} className="border rounded-lg p-4 bg-white shadow-sm flex flex-col">
                                                <div {...provided.dragHandleProps} className="self-center cursor-grab text-gray-400 hover:text-gray-600 mb-2">
                                                    <GripVertical size={20} />
                                                </div>
                                                <div className="flex-grow">
                                                    {banner.imageUrls?.desktop && <img src={banner.imageUrls.desktop} alt={banner.title} className="w-full h-32 object-cover rounded mb-3" />}
                                                    <h4 className="font-semibold text-lg">{banner.title}</h4>
                                                    <p className="text-gray-600 text-sm mb-3">{banner.subtitle}</p>
                                                    <div className="flex flex-wrap gap-2 items-center mb-3">
                                                        <span className="capitalize text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{banner.tipo}</span>
                                                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${banner.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                            {banner.status === 'active' ? 'Ativo' : 'Inativo/Rascunho'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex justify-between items-center border-t pt-3 mt-auto">
                                                    <button onClick={() => setSelectedBanner(banner)} className="action-icon-button text-gray-600" title="Ações" disabled={!hasHomePersonalizada}><MoreVertical size={18} /></button>
                                                    <span className="text-xs text-gray-500">Ordem: {banner.pedido}</span>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                {sortedBanners.length === 0 && <div className="col-span-full text-center py-12 text-gray-500">Nenhum banner encontrado.</div>}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>

            <ActionsDrawer
                open={!!selectedBanner}
                onClose={() => setSelectedBanner(null)}
                title={selectedBanner?.title || 'Banner'}
                actions={selectedBanner ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleEdit(selectedBanner),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedBanner._id),
                        destructive: true,
                    },
                ] : []}
            />

            <FabButton onClick={() => navigate('/site-institucional/banners/novo')} title="Novo Banner" icon={<Image size={26} className="icon" />} disabled={!hasHomePersonalizada} />
        </div>
    );
};

export default BannersHomePage;